import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils'
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical'
import { Dropdown, Button } from '@pandiafi/design-system'
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from '@lexical/list'
import { $createLinkNode, $isLinkNode } from '@lexical/link'
import {
  $createHeadingNode,
  $isHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'

import { getSelectedNode } from '../utils'
import { LinkFormModalValues, LinkFormModal } from '../LinkFormModal'

enum TextType {
  H1 = 'h1',
  H2 = 'h2',
  PARAGRAPH = 'paragraph',
  BULLET_LIST = 'bullet',
  NUMBER_LIST = 'number',
}

const textTypeDropdownOptions = [
  {
    id: TextType.H1,
    label: 'Otsikko 1',
  },
  {
    id: TextType.H2,
    label: 'Otsikko 2',
  },
  {
    id: TextType.PARAGRAPH,
    label: 'Leipäteksti',
  },
  {
    id: TextType.BULLET_LIST,
    label: 'Lista',
  },
  {
    id: TextType.NUMBER_LIST,
    label: 'Numeroitu lista',
  },
]

type Props = {
  imageTool?: boolean
}

export const ToolbarPlugin: React.FC<Props> = () => {
  const [editor] = useLexicalComposerContext()
  const toolbarRef = useRef(null)
  const [selectedBlockType, setSelectedBlockType] = useState<TextType>(
    TextType.PARAGRAPH
  )
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [newLink, setNewLink] = useState({ text: '', link: '' })

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection()

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent()
              return parent !== null && $isRootOrShadowRoot(parent)
            })

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow()
      }

      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)

      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(
            anchorNode,
            ListNode
          )
          const listType = parentList
            ? parentList.getListType()
            : element.getListType()

          if (listType === TextType.NUMBER_LIST) {
            setSelectedBlockType(TextType.NUMBER_LIST)
          } else {
            setSelectedBlockType(TextType.BULLET_LIST)
          }
        } else {
          const textType = $isHeadingNode(element)
            ? element.getTag()
            : element.getType()

          if (Object.values(TextType).includes(textType as TextType)) {
            setSelectedBlockType(textType as TextType)
          }
        }
      }
    }
  }, [editor])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar()
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar()
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload)
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload)
          return false
        },
        COMMAND_PRIORITY_LOW
      )
    )
  }, [editor, $updateToolbar])

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }

  const formatHeading = (headingSize: HeadingTagType) => {
    if (selectedBlockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection()
        $setBlocksType(selection, () => $createHeadingNode(headingSize))
      })
    }
  }

  const formatBulletList = () => {
    if (selectedBlockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      formatParagraph()
    }
  }

  const formatNumberedList = () => {
    if (selectedBlockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      formatParagraph()
    }
  }

  const getToolbarButtonStyle = (
    isSelected: boolean
  ): CSSProperties | undefined => {
    if (!isSelected) {
      return undefined
    }

    return {
      border: '1px solid var(--common-gray-4)',
      boxShadow: '0px 0px 3px 0px #979797',
    }
  }

  const handleAddLinkClick = () => {
    editor.read(() => {
      const selection = $getSelection()

      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection)
        const parent = node.getParent()
        const text = selection.getTextContent()

        const linkNode = $isLinkNode(node)
          ? node
          : $isLinkNode(parent)
            ? parent
            : null

        const link = $isLinkNode(linkNode) ? linkNode.getURL() : ''

        setNewLink({
          text,
          link,
        })
      }
    })

    setShowLinkModal(true)
  }

  const handleAddLink = (values: LinkFormModalValues) => {
    const { text, link } = values

    editor.update(() => {
      const selection = $getSelection()

      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection)
        const parent = node.getParent()
        const linkNode = $isLinkNode(node)
          ? node
          : $isLinkNode(parent)
            ? parent
            : null

        if (!linkNode) {
          // insert link with text
          $insertNodes([$createLinkNode(link).append($createTextNode(text))])
        } else {
          // update link url
          linkNode.setURL(link)

          // update link text
          linkNode.getChildren().some((child) => {
            if ($isTextNode(child) && child.getTextContent() === text) {
              child.setTextContent(text)
              return true
            }
            return false
          })
        }
      }
    })

    handleLinkModalClose()
  }

  const handleLinkModalClose = () => {
    setShowLinkModal(false)
    setNewLink({ text: '', link: '' })
  }

  return (
    <div
      className="bg-common-gray-3 rounded-t-md flex justify-between items-center p-2 border-b border-common-gray-5"
      ref={toolbarRef}
    >
      <div className="space-x-2">
        <Button
          disabled={!canUndo}
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          variant="icon"
          icon="undo"
          title="Kumoa"
        />
        <Button
          disabled={!canRedo}
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          variant="icon"
          icon="redo"
          title="Palauta"
        />
      </div>

      <div className="w-44">
        <Dropdown
          value={textTypeDropdownOptions.find(
            (option) => option.id === selectedBlockType
          )}
          onChange={(value) => {
            if (!value) return

            switch (value.id) {
              case TextType.H1:
                formatHeading('h1')
                break
              case TextType.H2:
                formatHeading('h2')
                break
              case TextType.PARAGRAPH:
                formatParagraph()
                break
              case TextType.BULLET_LIST:
                formatBulletList()
                break
              case TextType.NUMBER_LIST:
                formatNumberedList()
                break
              default:
                break
            }
          }}
          options={textTypeDropdownOptions}
        />
      </div>

      <div className="space-x-2">
        <Button
          style={getToolbarButtonStyle(isBold)}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          variant="icon"
          icon="bold"
          title="Lihavoi"
        />
        <Button
          style={getToolbarButtonStyle(isItalic)}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          variant="icon"
          icon="italic"
          title="Kursivoi"
        />
        <Button
          style={getToolbarButtonStyle(isUnderline)}
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
          }
          variant="icon"
          icon="underline"
          title="Alleviivaa"
        />
      </div>

      <div className="space-x-2">
        <Button
          variant="icon"
          icon="chain"
          onClick={handleAddLinkClick}
          title="Lisää linkki"
        />
      </div>

      <LinkFormModal
        show={showLinkModal}
        onClose={handleLinkModalClose}
        onSave={handleAddLink}
        value={newLink}
      />
    </div>
  )
}
